import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import * as React from "react";
import { Eye, EyeOff, CheckCircle2, AlertCircle, Mail, ArrowRight, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { PhoneInput, validateE164 } from "@/components/PhoneInput";
import { toast } from "@/lib/toast";
import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";
import { enablePush } from "@/lib/push/client";
import { isPushAllowedContext } from "@/lib/push/guard";
import { trackAuthEvent } from "@/lib/authFunnelClient";
import { rememberAuthMethod, flushAuthGa4Event } from "@/lib/ga4Auth";
import { isReservedDisplayName, reservedDisplayNameMessage } from "@/lib/reserved-names";
import { SiteShell } from "@/components/SiteShell";


// 🔒 منع Open Redirect: قبول مسارات داخليّة فقط (تبدأ بـ "/" ولا تبدأ بـ "//" أو "/\")
function safeInternalPath(raw: unknown): string {
  if (typeof raw !== "string" || raw.length === 0) return "/account";
  if (!raw.startsWith("/")) return "/account";
  if (raw.startsWith("//") || raw.startsWith("/\\")) return "/account";
  if (/^\/[a-z]+:/i.test(raw)) return "/account";
  return raw;
}

// 🔁 بعض مسارات العودة تحمل query (مثل موافقة OAuth: /.lovable/oauth/consent?authorization_id=…)
// وrouter.navigate لا يتعامل معها كمسار — نستخدم تنقّلاً كاملاً في هذه الحالة.
function needsHardRedirect(path: string) {
  return path.includes("?") || path.startsWith("/.");
}


export const Route = createFileRoute("/auth")({
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: safeInternalPath(s.redirect),
    mode: s.mode === "signup" ? "signup" : "signin",
  }),
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Sign in — Lecmo" },
      { name: "description", content: "Sign in to your Lecmo account to track orders, manage your wishlist, and access the ambassador program." },
      { property: "og:title", content: "Sign in — Lecmo" },
      { property: "og:description", content: "Access your Lecmo account, orders, and ambassador dashboard." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AuthPage() {
  const { user, loading } = useAuth();
  const { locale, dir } = useLocale();
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth" });
  const redirectPath = search.redirect;
  const isAdminFlow = redirectPath.startsWith("/admin");
  const initialMode: "signin" | "signup" = isAdminFlow ? "signin" : (search.mode as "signin" | "signup");
  // 🎯 Google هو الأساس — لا نفتح فورم البريد آليّاً إلّا للأدمن
  const prefersEmailFirst = isAdminFlow;
  const [mode, setMode] = React.useState<"signin" | "signup">(initialMode);
  const [showEmailForm, setShowEmailForm] = React.useState(prefersEmailFirst);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [appleLoading, setAppleLoading] = React.useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  // 🔑 كلمة المرور مخفيّة افتراضيّاً في signin — تُكشف بـ toggle ثانويّ. signup لا يستخدمها أصلاً.
  const [usePasswordMode, setUsePasswordMode] = React.useState(isAdminFlow);
  const [enableNotifs, setEnableNotifs] = React.useState(true);
  const pushSupported = React.useMemo(() => isPushAllowedContext(), []);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [authError, setAuthError] = React.useState<{
    raw: string;
    friendly: string;
    source: "email" | "google" | "apple";
    code?: string;
    status?: number;
    at: string;
  } | null>(null);

  React.useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    // 📊 تتبّع: المستخدم أكمل الدخول ووصل لصفحة /auth وهو مسجَّل
    trackAuthEvent({ eventType: "completed", mode, redirectPath: search.redirect, locale });
    // 📊 GA4: login / sign_up + ربط user_id لتوحيد الجلسات مع أحداث الشراء
    flushAuthGa4Event(mode);
    void import("@/lib/ga4").then(({ setGa4UserId }) => setGa4UserId(user.id));
    // 📊 Google Ads Enhanced Conversions: ربط بيانات المستخدم المجزّأة
    void import("@/lib/googleAdsTag").then(({ setGoogleAdsUserData }) =>
      setGoogleAdsUserData({ email: user.email, phone: user.phone }),
    );
    const go = (path: string) => {
      if (cancelled) return;
      if (needsHardRedirect(path)) window.location.href = path;
      else navigate({ to: path as "/account" });
    };
    // 🚚 لا يوجد redirect صريح → لو الحساب سائق نوجّهه لـ /driver لا /account
    if (search.redirect !== "/account") {
      go(search.redirect);
      return;
    }
    // القراءة مباشرة بالعميل: سياسة RLS «Drivers view own row» تسمح للسائق برؤية صفّه
    supabase
      .from("drivers")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error("[auth] driver lookup failed:", error);
        go(data ? "/driver" : "/account");
      });

    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate, search.redirect, mode, locale]);


  // 📊 تتبّع: زيارة صفحة /auth (مرّة واحدة لكل جلسة دخول)
  React.useEffect(() => {
    if (loading || user) return;
    trackAuthEvent({ eventType: "page_view", mode, redirectPath: search.redirect, locale });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  React.useEffect(() => {
    setShowEmailForm(prefersEmailFirst);
  }, [prefersEmailFirst]);

  const t = (en: string, ar: string, fr: string) =>
    locale === "ar" ? ar : locale === "fr" ? fr : en;

  const mapAuthError = (raw: string): string => {
    const msg = raw.toLowerCase();
    if (msg.includes("invalid login credentials"))
      return t("Incorrect email or password.", "البريد أو كلمة المرور غير صحيحة.", "Email ou mot de passe incorrect.");
    if (msg.includes("user already registered") || msg.includes("already been registered"))
      return t("This email is already registered — try signing in.", "هذا البريد مسجّل مسبقاً — جرّب تسجيل الدخول.", "Cet email est déjà enregistré — essayez de vous connecter.");
    if (msg.includes("profiles_phone_e164_unique") || (msg.includes("duplicate key") && msg.includes("phone_e164")) || msg.includes("23505"))
      return t("This phone is linked to another account. Sign in or use a different number.", "هذا الرقم مرتبط بحساب آخر. سجّل الدخول أو استخدم رقماً مختلفاً.", "Ce numéro est lié à un autre compte.");
    if (msg.includes("email not confirmed"))
      return t("Please confirm your email first (check your inbox).", "يرجى تأكيد بريدك أوّلاً (تحقّق من صندوق الوارد).", "Veuillez confirmer votre email d'abord.");
    if (msg.includes("password should be at least"))
      return t("Password must be at least 6 characters.", "كلمة المرور يجب أن تكون ٦ أحرف على الأقل.", "Le mot de passe doit comporter au moins 6 caractères.");
    if (msg.includes("rate limit") || msg.includes("over_email_send_rate_limit"))
      return t("We sent an email recently — wait a minute and try again.", "أرسلنا بريداً مؤخراً — انتظر دقيقة وحاول مجدّداً.", "Email envoyé récemment — attendez une minute.");
    if (msg.includes("network") || msg.includes("failed to fetch"))
      return t("Network error — check your connection.", "خطأ في الشبكة — تحقّق من اتصالك.", "Erreur réseau — vérifiez votre connexion.");
    if (msg.includes("popup_closed") || msg.includes("popup closed") || msg.includes("cancelled"))
      return t("Sign-in was cancelled.", "تمّ إلغاء تسجيل الدخول.", "Connexion annulée.");
    return raw;
  };

  // 🔗 وجهة عودة عامّة دائماً: نعود إلى /auth (صفحة عامّة) حاملين المسار المقصود
  // في search.redirect — ثمّ يحوّلنا effect الجلسة إليه بعد تأكيد الجلسة.
  // ممنوع تمرير مسار محميّ (مثل /admin/...) كـ redirect_uri لأنّ الجلسة لا تكون
  // جاهزة بعد فور العودة فيُلغى الدخول ويظهر «تمّ إلغاء تسجيل الدخول».
  const publicReturnUrl = () =>
    `${window.location.origin}/auth?redirect=${encodeURIComponent(search.redirect)}&mode=${mode}`;

  const clearMessages = () => {
    setAuthError(null);
    setSuccessMessage(null);
  };


  // 📨 إرسال Magic Link — للدخول أو لإنشاء حساب جديد (مع الاسم والهاتف)
  const sendMagicLink = async (forMode: "signin" | "signup") => {
    if (!email || !email.includes("@")) {
      toast.error(t("Enter a valid email.", "أدخل بريداً صالحاً.", "Entrez un email valide."));
      return;
    }
    if (forMode === "signup") {
      if (isReservedDisplayName(name)) {
        toast.error(reservedDisplayNameMessage(locale));
        return;
      }
      const phoneE164 = phone.trim();
      const phoneError = validateE164(phoneE164, locale);
      if (phoneError) {
        toast.error(phoneError);
        return;
      }
      const { data: avail, error: availErr } = await supabase.rpc("is_phone_available", { _phone: phoneE164 });
      if (!availErr && avail === false) {
        toast.error(t(
          "This phone is linked to another account. Sign in or use a different number.",
          "هذا الرقم مرتبط بحساب آخر. سجّل الدخول أو استخدم رقماً مختلفاً.",
          "Ce numéro est lié à un autre compte.",
        ));
        return;
      }
    }
    trackAuthEvent({ eventType: "method_clicked", method: "magic_link", mode: forMode, redirectPath: search.redirect, locale });
    rememberAuthMethod("magic_link");
    setMagicLinkLoading(true);
    clearMessages();
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: forMode === "signup",
          emailRedirectTo: publicReturnUrl(),
          data: forMode === "signup"
            ? {
                display_name: name || email.split("@")[0],
                phone_e164: phone.trim(),
              }
            : undefined,
        },
      });
      if (error) throw error;
      setSuccessMessage(t(
        "Check your email — we sent you a one-click sign-in link.",
        "افحص بريدك — أرسلنا لك رابط دخول بنقرة واحدة.",
        "Vérifiez votre email — lien de connexion envoyé.",
      ));
      toast.success(t("Login link sent!", "تمّ إرسال رابط الدخول!", "Lien envoyé!"), { duration: 6000 });
    } catch (err) {
      const errObj = err as { message?: string };
      const friendly = mapAuthError(errObj.message ?? String(err));
      toast.error(friendly);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // 🔐 تسجيل دخول بكلمة مرور (للمستخدمين القدامى أو الأدمن فقط)
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackAuthEvent({ eventType: "method_clicked", method: "password", mode, redirectPath: search.redirect, locale });
    rememberAuthMethod("password");
    setSubmitting(true);
    clearMessages();
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setSuccessMessage(t("Welcome back! Redirecting…", "مرحباً بعودتك! جارٍ التوجيه…", "Bon retour! Redirection…"));
      toast.success(t("Welcome back.", "مرحباً بعودتك.", "Bon retour."));
    } catch (err) {
      const errObj = err as { message?: string; code?: string; status?: number; name?: string };
      const raw = errObj.message ?? String(err);
      const friendly = mapAuthError(raw);
      const lower = raw.toLowerCase();
      console.error("[auth] sign-in error:", { raw, code: errObj.code, status: errObj.status, name: errObj.name });
      setAuthError({ raw, friendly, source: "email", code: errObj.code, status: errObj.status, at: new Date().toISOString() });
      if (lower.includes("invalid login credentials")) {
        toast.error(friendly, {
          description: t("Did you sign up with Google or a magic link?", "هل سجّلت سابقاً بـ Google أو رابط الدخول؟", "Vous êtes-vous inscrit avec Google ou un lien magique ?"),
        });
      } else {
        toast.error(friendly);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onOAuth = async (provider: "google" | "apple") => {
    const setLoading = provider === "google" ? setGoogleLoading : setAppleLoading;
    trackAuthEvent({ eventType: "method_clicked", method: provider, mode, redirectPath: search.redirect, locale });
    rememberAuthMethod(provider);
    setLoading(true);
    clearMessages();
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: publicReturnUrl(),
      });
      if (result.error) {
        const raw = result.error.message;
        const friendly = mapAuthError(raw);
        console.error(`[auth] ${provider} sign-in error:`, result.error);
        setAuthError({ raw, friendly, source: provider, at: new Date().toISOString() });
        toast.error(friendly);
        setLoading(false);
        return;
      }
      if (result.redirected) return;
      setSuccessMessage(t("Signed in! Redirecting…", "تمّ الدخول! جارٍ التوجيه…", "Connecté! Redirection…"));
      if (needsHardRedirect(search.redirect)) window.location.href = search.redirect;
      else navigate({ to: search.redirect as "/account" });

    } catch (err) {
      const errObj = err as { message?: string; code?: string; status?: number };
      const raw = errObj.message ?? String(err);
      const friendly = mapAuthError(raw);
      console.error(`[auth] ${provider} sign-in exception:`, err);
      setAuthError({ raw, friendly, source: provider, code: errObj.code, status: errObj.status, at: new Date().toISOString() });
      toast.error(friendly);
      setLoading(false);
    }
  };

  // ⚡ تفعيل الإشعارات بعد التوجيه (لو فعّل المستخدم checkbox في signup)
  React.useEffect(() => {
    if (user && enableNotifs && pushSupported && mode === "signup") {
      enablePush(locale === "ar" ? "ar" : "en").catch(() => { /* noop */ });
    }
  }, [user, enableNotifs, pushSupported, mode, locale]);
  // 🚀 مستخدم مسجَّل أصلاً (أو الجلسة قيد الفحص): لا نرسم فورم الدخول إطلاقاً
  // — تحويل فوريّ بلا وميض ولا شاشة «جارٍ تحميل التجربة».
  if (loading || user) {
    return (
      <div className="min-h-screen bg-background" aria-busy="true" dir={dir} />
    );
  }

  return (

    <SiteShell>
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-24" dir={dir}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="block text-center">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="t-h3 tracking-[var(--tracking-wider)]">LECMO</span>
            <span className="t-eyebrow tracking-[var(--tracking-wider)]">perfume</span>
          </span>
        </Link>

        {/* Title */}
        <h1 className="mt-10 text-center t-h2 text-foreground">
          {mode === "signin"
            ? t("Sign in", "تسجيل الدخول", "Connexion")
            : t("Create account", "إنشاء حساب", "Créer un compte")}
        </h1>
        <p className="mt-2 text-center t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground">
          {t("Quick & secure access", "دخول سريع وآمن", "Accès rapide et sécurisé")}
        </p>

        {/* Admin flow banner */}
        {isAdminFlow && (
          <div className="mt-6 border border-primary/40 bg-primary/5 p-4 text-center text-sm text-foreground">
            <p className="font-medium text-primary">{t("Admin access", "دخول الأدمن", "Accès admin")}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(
                "Sign in with your admin credentials below.",
                "سجّل الدخول ببيانات الأدمن أدناه.",
                "Connectez-vous avec vos identifiants admin.",
              )}
            </p>
          </div>
        )}

        {/* ✅ Success message */}
        {successMessage && (
          <div
            role="status"
            className="mt-6 flex items-center gap-3 rounded-none border border-emerald-500/40 bg-emerald-50/60 p-4 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200 animate-fade-up"
          >
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <p>{successMessage}</p>
          </div>
        )}

        {/* ❌ Error banner */}
        {authError && (
          <div
            role="alert"
            className="mt-6 rounded-none border border-destructive/40 bg-destructive/5 p-4 text-sm text-foreground animate-fade-up"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <p className="font-semibold text-destructive">
                    {authError.source === "google"
                      ? t("Google sign-in failed", "فشل الدخول بـ Google", "Échec connexion Google")
                      : authError.source === "apple"
                        ? t("Apple sign-in failed", "فشل الدخول بـ Apple", "Échec connexion Apple")
                        : t("Sign-in failed", "فشل تسجيل الدخول", "Échec de connexion")}
                  </p>
                  <p className="mt-1 text-foreground">{authError.friendly}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAuthError(null)}
                className="text-xs text-muted-foreground hover:text-foreground"
                aria-label={t("Dismiss", "إخفاء", "Fermer")}
              >
                ✕
              </button>
            </div>
            {import.meta.env.DEV && (
              <details className="mt-3 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none hover:text-foreground">
                  {t("Show technical details", "عرض التفاصيل التقنيّة", "Détails techniques")}
                </summary>
                <div className="mt-2 space-y-1 break-words font-mono">
                  <div><span className="text-muted-foreground/70">reason:</span> {authError.raw}</div>
                  {authError.code && <div><span className="text-muted-foreground/70">code:</span> {authError.code}</div>}
                  {authError.status !== undefined && <div><span className="text-muted-foreground/70">status:</span> {authError.status}</div>}
                  <div><span className="text-muted-foreground/70">at:</span> {authError.at}</div>
                </div>
              </details>
            )}
          </div>
        )}

        {/* OAuth buttons */}
        <div className="mt-8 space-y-3">
          {/* Google */}
          <button
            onClick={() => onOAuth("google")}
            disabled={googleLoading || appleLoading}
            className={cn(
              "flex w-full items-center justify-center gap-3 rounded-none py-4 text-sm font-medium  transition-all disabled:opacity-50",
              "bg-foreground text-background hover:opacity-90",
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>
              {googleLoading
                ? t("Connecting…", "جارٍ الاتصال…", "Connexion…")
                : t("Continue with Google", "متابعة بـ Google", "Continuer avec Google")}
            </span>
          </button>

          {/* Apple */}
          <button
            onClick={() => onOAuth("apple")}
            disabled={appleLoading || googleLoading}
            className={cn(
              "flex w-full items-center justify-center gap-3 rounded-none border border-border py-4 text-sm font-medium transition-all disabled:opacity-50",
              "bg-background text-foreground hover:bg-secondary",
            )}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <span>
              {appleLoading
                ? t("Connecting…", "جارٍ الاتصال…", "Connexion…")
                : t("Continue with Apple", "متابعة بـ Apple", "Continuer avec Apple")}
            </span>
          </button>
        </div>

        {/* Email section */}
        {!showEmailForm ? (
          <button
            onClick={() => setShowEmailForm(true)}
            className="mt-6 flex w-full items-center justify-center gap-2 text-center t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground transition-colors hover:text-foreground"
          >
            <Mail className="h-3.5 w-3.5" />
            {t("Use email instead", "أو استخدم البريد الإلكتروني", "Utiliser l'email")}
          </button>
        ) : (
          <>
            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="t-eyebrow-muted tracking-[var(--tracking-wider)]">
                {t("or email", "أو البريد", "ou email")}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* الحقول المشتركة */}
            <div className="space-y-3">
              {mode === "signup" && (
                <Input
                  placeholder={t("Display name", "اسم العرض", "Nom d'affichage")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11"
                />
              )}
              <Input
                type="email"
                required
                placeholder={t("Email", "البريد الإلكتروني", "Email")}
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                className="h-11"
              />
              {mode === "signup" && (
                <div className="space-y-1">
                  <PhoneInput value={phone} onChange={(v) => { setPhone(v); clearMessages(); }} />
                  <p className="text-[11px] text-muted-foreground px-1">
                    {t("Required — used for order updates.", "إلزاميّ — لتحديثات الطلبات.", "Obligatoire — pour le suivi des commandes.")}
                  </p>
                </div>
              )}

              {/* 🔐 كلمة المرور — تظهر فقط في signin مع usePasswordMode (للمستخدمين القدامى أو الأدمن) */}
              {mode === "signin" && usePasswordMode && (
                <form onSubmit={onSubmit} className="space-y-3">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={6}
                      placeholder={t("Password", "كلمة المرور", "Mot de passe")}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); clearMessages(); }}
                      className="h-11 pe-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t("Hide password", "إخفاء", "Masquer") : t("Show password", "إظهار", "Afficher")}
                      className="absolute end-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 border border-border bg-secondary py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition-colors hover:bg-secondary/70 disabled:opacity-50"
                  >
                    {submitting ? (
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <>
                        <span>{t("Sign in with password", "دخول بكلمة المرور", "Se connecter par mot de passe")}</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {mode === "signup" && pushSupported && (
                <label className="flex cursor-pointer items-start gap-2.5 rounded-none border border-border bg-secondary/30 px-3 py-2.5 text-start transition-colors hover:bg-secondary/50">
                  <input
                    type="checkbox"
                    checked={enableNotifs}
                    onChange={(e) => setEnableNotifs(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span className="flex flex-1 items-start gap-2">
                    <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-[13px] leading-snug text-foreground">
                      {t(
                        "Notify me about my orders and new arrivals.",
                        "أرسلوا لي تنبيهات الطلبات والإصدارات الجديدة.",
                        "M'avertir des commandes et nouveautés.",
                      )}
                    </span>
                  </span>
                </label>
              )}

              {/* ✨ Magic Link — الزرّ الأساسيّ للبريد (signin أو signup) */}
              {!(mode === "signin" && usePasswordMode) && (
                <button
                  type="button"
                  onClick={() => sendMagicLink(mode)}
                  disabled={magicLinkLoading || !email}
                  className="flex w-full items-center justify-center gap-2 bg-foreground py-4 text-sm font-medium text-background  transition-all hover:opacity-90 disabled:opacity-50"
                >
                  {magicLinkLoading ? (
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      <span>
                        {mode === "signup"
                          ? t("Create account with a magic link", "إنشاء حساب برابط سحريّ", "Créer avec un lien magique")
                          : t("Email me a one-click sign-in link", "أرسل لي رابط دخول بنقرة واحدة", "Envoyez-moi un lien de connexion")}
                      </span>
                    </>
                  )}
                </button>
              )}

              {/* روابط ثانويّة — فقط في signin */}
              {mode === "signin" && (
                <div className="pt-2 text-center text-xs">
                  <button
                    type="button"
                    onClick={() => { setUsePasswordMode((v: boolean) => !v); clearMessages(); }}
                    className="t-link"
                  >
                    {usePasswordMode
                      ? t("Use a magic link instead", "استخدم رابطاً سحريّاً بدلاً", "Utiliser un lien magique")
                      : t("I have a password — use it instead", "لديّ كلمة مرور — استخدمها", "J'ai un mot de passe")}
                  </button>
                  {usePasswordMode && (
                    <p className="mt-2 text-muted-foreground">
                      <Link to="/forgot-password" className="t-link">
                        {t("Forgot password?", "نسيت كلمة المرور؟", "Mot de passe oublié ?")}
                      </Link>
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Switch mode */}
        <p className="mt-8 text-center text-xs text-muted-foreground">
          {mode === "signin"
            ? t("New here?", "جديد هنا؟", "Nouveau ici ?")
            : t("Already have an account?", "لديك حساب؟", "Vous avez déjà un compte ?")}{" "}
          <button
            onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); clearMessages(); }}
            className="t-link"
          >
            {mode === "signin"
              ? t("Create one", "أنشئ حساباً", "Créer un compte")
              : t("Sign in", "تسجيل الدخول", "Connexion")}
          </button>
        </p>
      </div>
    </div>
    </SiteShell>
  );
}
